// src/components/modals/AllReportsModal.jsx
import { useState } from 'react'
import { X, FileText, Calendar, TrendingUp, RotateCcw, Package } from 'lucide-react'
import ReportService from '../../services/reportService'
// Note: Install thermal printer SDK - Examples:
// npm install @xprinter/epos-print-sdk
// npm install escpos escpos-network
// import * as ePosDev from '@xprinter/epos-print-sdk'

const AllReportsModal = ({ isOpen, onClose, branchId, branchInfo }) => {
  const [loading, setLoading] = useState(false)
  const [selectedReport, setSelectedReport] = useState(null)
  
  // Removed printer connection state - now using direct PDF download

  if (!isOpen) return null

  // Thermal printer optimized styles
  const getThermalPrintCSS = () => {
    return `
      @media print {
        body { margin: 0; padding: 0; }
        .no-print { display: none !important; }
      }
      body {
        font-family: 'Courier New', monospace !important;
        font-size: 14px !important;
        line-height: 1.4 !important;
        color: black !important;
        background: white !important;
        width: 80mm !important;
        margin: 0 !important;
        padding: 8px !important;
      }
      .header {
        text-align: center !important;
        margin-bottom: 16px !important;
        font-weight: bold !important;
      }
      .title {
        font-size: 18px !important;
        font-weight: bold !important;
        margin-bottom: 4px !important;
      }
      .section {
        margin-bottom: 12px !important;
      }
      .separator {
        border-top: 1px dashed black !important;
        margin: 8px 0 !important;
        padding-top: 8px !important;
        font-weight: bold !important;
      }
      table {
        width: 100% !important;
        border-collapse: collapse !important;
        margin-bottom: 8px !important;
      }
      th, td {
        padding: 2px 4px !important;
        font-size: 12px !important;
        border: none !important;
      }
      th {
        font-weight: bold !important;
        border-bottom: 1px solid black !important;
      }
      .right {
        text-align: right !important;
      }
      .row {
        display: flex !important;
        justify-content: space-between !important;
        margin: 2px 0 !important;
        font-weight: 500 !important;
      }
      .total-row {
        font-weight: bold !important;
        border-top: 1px solid black !important;
        padding-top: 4px !important;
        margin-top: 8px !important;
      }
      .footer {
        text-align: center !important;
        margin-top: 16px !important;
        font-size: 12px !important;
      }
    `
  }

  const generateSalesReportHTML = (salesData, branchInfo) => {
    const currentDate = new Date().toLocaleDateString('en-GB')
    const currentTime = new Date().toLocaleTimeString('en-GB')

    return `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8">
          <title>Sales Report - TODAY (${currentDate})</title>
          <style>${getThermalPrintCSS()}</style>
        </head>
        <body>
          <div class="header">
            <div class="title">${branchInfo?.name || 'TEZLAA CAFE'}</div>
            <div>SALES REPORT - TODAY</div>
            <div>${currentDate} at ${currentTime}</div>
          </div>

          <div class="section">
            <div class="separator">SALES DETAILS</div>
            <table>
              <thead>
                <tr>
                  <th>Order ID</th>
                  <th class="right">Amount</th>
                  <th class="right">Discount</th>
                </tr>
              </thead>
              <tbody>
                ${salesData.data.orders.map(order => `
                  <tr>
                    <td>${order.orderId}</td>
                    <td class="right">Rs.${order.amount.toFixed(2)}</td>
                    <td class="right">Rs.${order.discount.toFixed(2)}</td>
                  </tr>
                `).join('')}
              </tbody>
            </table>
            
            <div class="separator">-------------------------</div>
            
            <div class="row total-row">
              <span>Total Sales:</span>
              <span>Rs.${salesData.data.totals.totalSales.toFixed(2)}</span>
            </div>
            <div class="row">
              <span>Total Discounts:</span>
              <span>Rs.${salesData.data.totals.totalDiscounts.toFixed(2)}</span>
            </div>
            <div class="row total-row">
              <span>Gross Sales:</span>
              <span>Rs.${salesData.data.totals.grossSales.toFixed(2)}</span>
            </div>
          </div>

          <div class="footer">
            <div>Printed: ${new Date().toLocaleString()}</div>
            <div>Thank you!</div>
          </div>
        </body>
      </html>
    `
  }

  const generateStockReportHTML = (stockData, branchInfo) => {
    const currentDate = new Date().toLocaleDateString('en-GB')
    const currentTime = new Date().toLocaleTimeString('en-GB')

    return `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8">
          <title>Stock Status Report (${currentDate})</title>
          <style>${getThermalPrintCSS()}</style>
        </head>
        <body>
          <div class="header">
            <div class="title">${branchInfo?.name || 'TEZLAA CAFE'}</div>
            <div>STOCK STATUS REPORT</div>
            <div>${currentDate} at ${currentTime}</div>
          </div>

          <div class="section">
            <div class="separator">STOCK STATUS REPORT</div>
            <table>
              <thead>
                <tr>
                  <th>Item</th>
                  <th>Code</th>
                  <th class="right">Stock</th>
                  <th class="right">Min</th>
                </tr>
              </thead>
              <tbody>
                ${stockData.data.items.map(item => `
                  <tr>
                    <td>${item.itemName}</td>
                    <td>${item.itemCode}</td>
                    <td class="right">${item.currentStock} ${item.unit}</td>
                    <td class="right">${item.threshold}</td>
                  </tr>
                `).join('')}
              </tbody>
            </table>
            
            <div class="separator">-------------------------</div>
            
            <div class="row">
              <span>Total Items:</span>
              <span>${stockData.data.summary.totalItems}</span>
            </div>
            <div class="row">
              <span>Products:</span>
              <span>${stockData.data.summary.products}</span>
            </div>
            <div class="row">
              <span>Raw Materials:</span>
              <span>${stockData.data.summary.rawMaterials}</span>
            </div>
            <div class="row">
              <span>Low Stock:</span>
              <span>${stockData.data.summary.lowStock}</span>
            </div>
            <div class="row total-row">
              <span>Out of Stock:</span>
              <span>${stockData.data.summary.outOfStock}</span>
            </div>
          </div>

          <div class="footer">
            <div>Printed: ${new Date().toLocaleString()}</div>
            <div>Thank you!</div>
          </div>
        </body>
      </html>
    `
  }

  const generateReturnsReportHTML = (returnsData, branchInfo) => {
    const currentDate = new Date().toLocaleDateString('en-GB')
    const currentTime = new Date().toLocaleTimeString('en-GB')

    return `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8">
          <title>Returns Report - TODAY (${currentDate})</title>
          <style>${getThermalPrintCSS()}</style>
        </head>
        <body>
          <div class="header">
            <div class="title">${branchInfo?.name || 'TEZLAA CAFE'}</div>
            <div>RETURNS REPORT - TODAY</div>
            <div>${currentDate} at ${currentTime}</div>
          </div>

          <div class="section">
            <div class="separator">RETURNS DETAILS</div>
            <table>
              <thead>
                <tr>
                  <th>Return ID</th>
                  <th>Items</th>
                  <th class="right">Amount</th>
                </tr>
              </thead>
              <tbody>
                ${returnsData.data.returns.map(returnItem => `
                  <tr>
                    <td>${returnItem.returnId}</td>
                    <td>${returnItem.numberOfItems}</td>
                    <td class="right">Rs.${returnItem.returnAmount.toFixed(2)}</td>
                  </tr>
                `).join('')}
              </tbody>
            </table>
            
            <div class="separator">-------------------------</div>
            
            <div class="row total-row">
              <span>Total Returns:</span>
              <span>Rs.${returnsData.data.totals.totalReturns.toFixed(2)}</span>
            </div>
            <div class="row">
              <span>Total Items:</span>
              <span>${returnsData.data.totals.totalItems}</span>
            </div>
          </div>

          <div class="footer">
            <div>Printed: ${new Date().toLocaleString()}</div>
            <div>Thank you!</div>
          </div>
        </body>
      </html>
    `
  }

  // Direct download as PDF using browser print dialog in same tab
  const printReport = (htmlContent) => {
    // Create a hidden iframe
    const iframe = document.createElement('iframe')
    iframe.style.position = 'fixed'
    iframe.style.right = '0'
    iframe.style.bottom = '0'
    iframe.style.width = '0'
    iframe.style.height = '0'
    iframe.style.border = 'none'
    
    document.body.appendChild(iframe)
    
    // Write content to iframe
    const iframeDoc = iframe.contentWindow.document
    iframeDoc.open()
    iframeDoc.write(htmlContent)
    iframeDoc.close()
    
    // Trigger print dialog
    setTimeout(() => {
      iframe.contentWindow.focus()
      iframe.contentWindow.print()
      
      // Clean up after print dialog closes
      setTimeout(() => {
        document.body.removeChild(iframe)
      }, 1000)
    }, 250)
  }

  const handleSalesReport = async () => {
    if (!branchId) {
      alert('Branch ID is required')
      return
    }

    setLoading(true)
    setSelectedReport('sales')

    try {
      const salesData = await ReportService.getSalesReport(branchId)
      
      if (salesData.success) {
        const htmlContent = generateSalesReportHTML(salesData, branchInfo)
        printReport(htmlContent)
      } else {
        alert('Failed to generate sales report: ' + salesData.message)
      }
    } catch (error) {
      console.error('Error generating sales report:', error)
      alert('Error generating sales report')
    } finally {
      setLoading(false)
      setSelectedReport(null)
    }
  }

  const handleReturnsReport = async () => {
    if (!branchId) {
      alert('Branch ID is required')
      return
    }

    setLoading(true)
    setSelectedReport('returns')

    try {
      const returnsData = await ReportService.getReturnsReport(branchId)
      
      if (returnsData.success) {
        const htmlContent = generateReturnsReportHTML(returnsData, branchInfo)
        printReport(htmlContent)
      } else {
        alert('Failed to generate returns report: ' + returnsData.message)
      }
    } catch (error) {
      console.error('Error generating returns report:', error)
      alert('Error generating returns report')
    } finally {
      setLoading(false)
      setSelectedReport(null)
    }
  }

  const handleStockReport = async () => {
    if (!branchId) {
      alert('Branch ID is required')
      return
    }

    setLoading(true)
    setSelectedReport('stock')

    try {
      const stockData = await ReportService.getStockReport(branchId)
      
      if (stockData.success) {
        const htmlContent = generateStockReportHTML(stockData, branchInfo)
        printReport(htmlContent)
      } else {
        alert('Failed to generate stock report: ' + stockData.message)
      }
    } catch (error) {
      console.error('Error generating stock report:', error)
      alert('Error generating stock report')
    } finally {
      setLoading(false)
      setSelectedReport(null)
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between rounded-t-lg">
          <div className="flex items-center">
            <FileText className="h-6 w-6 text-blue-600 mr-3" />
            <div>
              <h2 className="text-xl font-semibold text-gray-900">All Reports - TODAY</h2>
              <p className="text-sm text-gray-500">
                {branchInfo?.name || 'Branch'} • {new Date().toLocaleDateString('en-GB')}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Printer Settings */}
          <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
            {/* Reports Info */}
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-start">
                <FileText className="h-5 w-5 text-blue-600 mr-2 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-blue-900">Download Reports</p>
                  <p className="text-xs text-blue-700 mt-1">
                    Click on any report below to open the print dialog. You can save as PDF or print directly.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            {/* Sales Report */}
            <button
              onClick={handleSalesReport}
              disabled={loading}
              className={`w-full p-4 text-left border-2 rounded-lg transition-colors ${
                selectedReport === 'sales' && loading
                  ? 'border-orange-500 bg-orange-50'
                  : 'border-gray-200 hover:border-orange-300 hover:bg-orange-50'
              } ${loading && selectedReport !== 'sales' ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <TrendingUp className="h-5 w-5 text-orange-600 mr-3" />
                  <div>
                    <div className="font-medium">Sales Report - TODAY</div>
                    <div className="text-sm text-gray-500">Order ID, Amount, Discount</div>
                  </div>
                </div>
                {selectedReport === 'sales' && loading ? (
                  <div className="flex items-center">
                    <div className="w-4 h-4 border-2 border-orange-600 border-t-transparent rounded-full animate-spin mr-2" />
                    <span className="text-sm text-orange-600">Processing...</span>
                  </div>
                ) : (
                  <FileText className="h-5 w-5 text-gray-400" />
                )}
              </div>
            </button>

            {/* Returns Report */}
            <button
              onClick={handleReturnsReport}
              disabled={loading}
              className={`w-full p-4 text-left border-2 rounded-lg transition-colors ${
                selectedReport === 'returns' && loading
                  ? 'border-red-500 bg-red-50'
                  : 'border-gray-200 hover:border-red-300 hover:bg-red-50'
              } ${loading && selectedReport !== 'returns' ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <RotateCcw className="h-5 w-5 text-red-600 mr-3" />
                  <div>
                    <div className="font-medium">Returns Report - TODAY</div>
                    <div className="text-sm text-gray-500">Return ID, Items, Amount</div>
                  </div>
                </div>
                {selectedReport === 'returns' && loading ? (
                  <div className="flex items-center">
                    <div className="w-4 h-4 border-2 border-red-600 border-t-transparent rounded-full animate-spin mr-2" />
                    <span className="text-sm text-red-600">Processing...</span>
                  </div>
                ) : (
                  <FileText className="h-5 w-5 text-gray-400" />
                )}
              </div>
            </button>

            {/* Stock Report */}
            <button
              onClick={handleStockReport}
              disabled={loading}
              className={`w-full p-4 text-left border-2 rounded-lg transition-colors ${
                selectedReport === 'stock' && loading
                  ? 'border-green-500 bg-green-50'
                  : 'border-gray-200 hover:border-green-300 hover:bg-green-50'
              } ${loading && selectedReport !== 'stock' ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <Package className="h-5 w-5 text-green-600 mr-3" />
                  <div>
                    <div className="font-medium">Stock Status Report</div>
                    <div className="text-sm text-gray-500">Current stock levels and status</div>
                  </div>
                </div>
                {selectedReport === 'stock' && loading ? (
                  <div className="flex items-center">
                    <div className="w-4 h-4 border-2 border-green-600 border-t-transparent rounded-full animate-spin mr-2" />
                    <span className="text-sm text-green-600">Processing...</span>
                  </div>
                ) : (
                  <FileText className="h-5 w-5 text-gray-400" />
                )}
              </div>
            </button>
          </div>

          {/* Info Note */}
          <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-start">
              <Calendar className="h-5 w-5 text-blue-600 mr-2 mt-0.5" />
              <div className="text-sm text-blue-800">
                <div className="font-medium">Today's Data Only</div>
                <div>All reports will show data for today ({new Date().toLocaleDateString('en-GB')}) only. Reports are optimized for thermal printing.</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AllReportsModal